import React, { useState, useEffect } from "react";
import { ArrowLeft, Plus, Trash2, Edit2, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { showError, showSuccess } from "@/utils/toast";

interface ActionPreset {
    id: number;
    name: string;
    code: string;
    action_type: string;
    description?: string;
    is_active: boolean;
}

const ActionPresetsPage: React.FC = () => {
    const navigate = useNavigate();
    const [presets, setPresets] = useState<ActionPreset[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // New preset form
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        code: "",
        action_type: "percentage",
        description: "",
    });
    const [editingId, setEditingId] = useState<number | null>(null);

    const actionTypes = [
        { value: "percentage", label: "Százalék (%)" },
        { value: "fixed_amount", label: "Fix ár (€)" },
        { value: "ratio", label: "Arány (2:1, 3:2)" },
        { value: "bulk", label: "Minimum mennyiség" },
        { value: "tiered", label: "Lépcsőzött" },
    ];

    useEffect(() => {
        fetchPresets();
    }, []);

    const fetchPresets = async () => {
        setLoading(true);
        try {
            const response = await fetch("/api/admin/action_presets");
            if (response.ok) {
                const data = await response.json();
                setPresets(data.action_presets || []);
            } else {
                showError("Nem sikerült az akciósablonok betöltése!");
            }
        } catch (error) {
            console.error("Error fetching presets:", error);
            showError("Hiba az akciósablonok betöltésekor!");
        } finally {
            setLoading(false);
        }
    };

    const handleAddOrUpdate = async () => {
        if (!formData.name.trim() || !formData.code.trim()) {
            showError("Adjon meg nevet és kódot!");
            return;
        }

        setSubmitting(true);
        try {
            const method = editingId ? "PATCH" : "POST";
            const url = editingId
                ? `/api/admin/action_presets/${editingId}`
                : "/api/admin/action_presets";

            const response = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action_preset: {
                        name: formData.name,
                        code: formData.code,
                        action_type: formData.action_type,
                        description: formData.description,
                        is_active: true,
                    },
                }),
            });

            if (response.ok) {
                showSuccess(editingId ? "Akciósablonok frissítve!" : "Akciósablonok hozzáadva!");
                setFormData({ name: "", code: "", action_type: "percentage", description: "" });
                setEditingId(null);
                setShowForm(false);
                await fetchPresets();
            } else {
                showError("Hiba az akciósablonok mentésekor!");
            }
        } catch (error) {
            console.error("Error:", error);
            showError("Hiba az akciósablonok mentésekor!");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Biztosan törlöd ezt az akciósablont?")) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/action_presets/${id}`, {
                method: "DELETE",
            });

            if (response.ok) {
                showSuccess("Akciósablonok törölve!");
                await fetchPresets();
            } else {
                showError("Hiba az akciósablonok törlésekkor!");
            }
        } catch (error) {
            console.error("Error:", error);
            showError("Hiba az akciósablonok törlésekkor!");
        }
    };

    const handleEdit = (preset: ActionPreset) => {
        setFormData({
            name: preset.name,
            code: preset.code,
            action_type: preset.action_type,
            description: preset.description || "",
        });
        setEditingId(preset.id);
        setShowForm(true);
    };

    const handleCancel = () => {
        setShowForm(false);
        setFormData({ name: "", code: "", action_type: "percentage", description: "" });
        setEditingId(null);
    };

    if (loading) {
        return <div className="text-center py-8">Betöltés...</div>;
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate("/admin")}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold">Akciósablonok</h1>
                        <p className="text-sm text-muted-foreground">Új akciósablonok hozzáadása és kezelése</p>
                    </div>
                </div>
                <Button
                    onClick={() => setShowForm(!showForm)}
                    className="gap-2"
                >
                    <Plus className="h-4 w-4" />
                    Új akciósablonok
                </Button>
            </div>

            {/* New/Edit Form */}
            {showForm && (
                <Card className="bg-blue-50 dark:bg-slate-900 border-blue-200 dark:border-slate-700">
                    <CardHeader>
                        <CardTitle className="text-lg">
                            {editingId ? "Akciósablonok szerkesztése" : "Új akciósablonok"}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label className="text-sm font-medium">Akció neve *</Label>
                            <Input
                                placeholder="pl. 2:1 akció, 20% kedvezmény"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({ ...formData, name: e.target.value })
                                }
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <Label className="text-sm font-medium">Kód *</Label>
                            <Input
                                placeholder="pl. BUY_2_GET_1, DISCOUNT_20"
                                value={formData.code}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        code: e.target.value.toUpperCase(),
                                    })
                                }
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <Label className="text-sm font-medium">Típus *</Label>
                            <Select
                                value={formData.action_type}
                                onValueChange={(val) =>
                                    setFormData({ ...formData, action_type: val })
                                }
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {actionTypes.map((type) => (
                                        <SelectItem key={type.value} value={type.value}>
                                            {type.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-sm font-medium">Leírás (opcionális)</Label>
                            <Input
                                placeholder="pl. Vásárlás 2, kapjon 1-et ingyen"
                                value={formData.description}
                                onChange={(e) =>
                                    setFormData({ ...formData, description: e.target.value })
                                }
                                className="mt-1"
                            />
                        </div>

                        <div className="flex gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={handleCancel}
                                disabled={submitting}
                            >
                                Mégse
                            </Button>
                            <Button
                                onClick={handleAddOrUpdate}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        Mentés...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="h-4 w-4 mr-2" />
                                        {editingId ? "Frissítés" : "Hozzáadás"}
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Presets List */}
            <div className="space-y-3">
                <h2 className="text-xl font-semibold">Akciósablonok ({presets.length})</h2>
                {presets.length === 0 ? (
                    <div className="text-center py-12 bg-muted/30 rounded-lg">
                        <p className="text-muted-foreground">Még nincs akciósablonok</p>
                    </div>
                ) : (
                    <div className="grid gap-3">
                        {presets.map((preset) => (
                            <Card
                                key={preset.id}
                                className="hover:shadow-md transition-shadow"
                            >
                                <CardContent className="pt-6 flex items-center justify-between">
                                    <div className="flex-1">
                                        <h3 className="font-semibold">{preset.name}</h3>
                                        <p className="text-sm text-muted-foreground">
                                            Kód: <span className="font-mono">{preset.code}</span> • Típus:{" "}
                                            <span className="font-mono">
                                                {actionTypes.find((t) => t.value === preset.action_type)
                                                    ?.label || preset.action_type}
                                            </span>
                                        </p>
                                        {preset.description && (
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {preset.description}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEdit(preset)}
                                        >
                                            <Edit2 className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => handleDelete(preset.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActionPresetsPage;
